import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

const SAFEWORD = 'safeword';

/**
 * Small "type to confirm" modal used everywhere a person can block a user
 * (profile page, posts/blogs/events listings, and their detail pages).
 *
 * Requires the viewer to type the literal word "Safeword" (case-insensitive
 * — compared via a trimmed, lower-cased match) before the block goes
 * through. Blocking fires the instant the typed text matches — no separate
 * confirm click required — but the Block button also works the same way
 * for anyone who prefers pressing a button (and for keyboard/screen-reader
 * users tabbing through the form).
 */
@Component({
  selector: 'app-block-user-confirm',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './block-user-confirm.html',
  // Everything this renders is `position: fixed`, so the host must not
  // generate a box — see ConfirmationPopup for the same reasoning (this
  // gets dropped inside masonry/grid layouts on the listing pages too).
  styles: [':host { display: contents; }'],
})
export class BlockUserConfirm implements OnChanges {

  @Input() isOpen = false;
  /** Optional — shown under the title as "You're about to block {name}". */
  @Input() targetName = '';

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  typedWord = '';
  showError = false;
  private hasConfirmed = false;

  ngOnChanges(changes: SimpleChanges): void {
    // Reset the form every time the modal is (re-)opened, so a previous
    // attempt (or a previous target user) never leaks into the next one.
    if (changes['isOpen'] && this.isOpen) {
      this.typedWord = '';
      this.showError = false;
      this.hasConfirmed = false;
    }
  }

  get isMatch(): boolean {
    return this.typedWord.trim().toLowerCase() === SAFEWORD;
  }

  onTypedWordChange(): void {
    this.showError = false;
    if (this.isMatch && !this.hasConfirmed) {
      this.hasConfirmed = true;
      this.confirmed.emit();
    }
  }

  onSubmit(): void {
    if (!this.isMatch) {
      this.showError = true;
      return;
    }
    if (this.hasConfirmed) return;
    this.hasConfirmed = true;
    this.confirmed.emit();
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onBackdropClick(): void {
    this.cancelled.emit();
  }
}
